<template>
  <div ref="am-wrapper" class="am-Wrapper">
    <AppMenuTrigger :menu-expanded="isMenuExpanded" @toggle-menu="toggleMenu" />

    <div
      :class="{
        'am-ContentWrapper-collapsed': isMenuCollapsed,
        'am-ContentWrapper-truncating': isMenuTruncating
      }"
      class="am-ContentWrapper"
    >
      <div ref="menuReference" :class="classes">
        <div class="am-Content_Head">
          <AppMenuLogo />
          <AppMenuWorkspaceSelect
            v-if="!hideWorkspaceSelect"
            :disabled="disabled"
            :menu-expanded="isMenuExpanded"
          />

          <AppDivider v-else no-margin />

          <AppVerticalScrollGradient :arrived-state="arrivedState" class="am-Gradient" no-border />
        </div>
        <div ref="menu-body" class="am-Content_Body">
          <AppMenuNav
            v-model:some-additional-menu-opened="someAdditionalMenuOpened"
            :disabled="disabled"
          />
        </div>
        <div v-if="showInternalBillingBanners" class="am-WebAppMenuContent">
          <UpgradeTierReminder
            v-if="isOwner && isOverLimitedLicenseStatus && !isBillingModuleDisabled"
          />
          <BillingReminder v-else-if="isLicenseTrialStatus && !isBillingModuleDisabled" />
          <AppBannerWrapper v-else />

          <InviteUsersToWorkspaceModal v-if="!isBillingPage">
            <template #trigger="{ onTriggerClick }">
              <AppButton
                class="am-InviteUsersTrigger"
                icon="invite-user"
                type="subtle"
                @click="onTriggerClick"
              >
                {{ $t('users.invite') }}
              </AppButton>
            </template>
          </InviteUsersToWorkspaceModal>
        </div>
        <!--   в слоте передает баннер для джира лицензий, из App.vue     -->
        <slot v-if="!shouldHideBanners" name="jiraAppLicenseBanner">
          <!--    Если не передается банер, то отображаем дефолтные     -->
          <AppBannerWrapper v-if="isJiraApp" />
        </slot>

        <AppVerticalScrollGradient
          :arrived-state="arrivedState"
          class="am-Gradient"
          is-bottom-placement
        />
      </div>

      <AutomationsPromoBanner v-if="isShowPromotionBanner" class="am-PromotionBanner" />

      <div class="am-Content_Footer">
        <AppMenuNav
          v-model:some-additional-menu-opened="someAdditionalMenuOpened"
          :disabled="disabled"
          :layout="APP_MENU_NAV_LAYOUTS.HORIZONTAL"
          :placement="NAV_ITEM_PLACEMENTS.BOTTOM"
          style="--gap: 0"
          @item-click="onMenuItemClick"
        >
          <template #list-prepend>
            <li>
              <AppMenuUserActions @create-organization="isShowCreateOrganizationModal = true" />
            </li>
            <li v-if="isPortalHasContent && isMenuExpanded">
              <portal-target :name="BOTTOM_NAV_NOTIFICATIONS_PORTAL_NAME" />
            </li>
          </template>
        </AppMenuNav>
      </div>
    </div>
  </div>
  <SupportModal v-model:show="isShowSupportModal" />

  <portal to="modal-windows">
    <Modal
      :scrollable-wrapper="false"
      :show="isShowReleaseUpdatesModal"
      class="am-ReleaseUpdatesModal"
      manual-close
      size="lg-next"
      @close="onReleaseUpdatesModalClose"
    >
      <template #header>
        <div class="am-ModalHead">
          <AppTitle :level="3" class="am-ModalHead_Title" disable-margin>
            {{ $t('menu.whats_new') }}
          </AppTitle>
        </div>
      </template>

      <template #before-close>
        <AppHrefLink
          :href="currentChangeLogLinks.SOURCE"
          class="am-ModalLink"
          prepend-icon="shortcut-next"
        >
          {{ $t('release_updates.link') }}
        </AppHrefLink>
      </template>
      <template #modal-body>
        <ReleaseUpdatesLoader v-if="isLoading" class="am-ModalBody_Loader" />
        <iframe
          v-show="!isLoading"
          :src="`${currentChangeLogLinks.IFRAME}&timestamp=${timestamp}`"
          class="am-ModalBody_Iframe"
          frameborder="0"
          @load="onIframeLoaded"
        />
      </template>

      <template #modal-footer>
        <div class="am-ModalFooter">
          {{ $t('release_updates.footer_text') }}
          <AppSocialMediaLinks />
        </div>
      </template>
    </Modal>

    <ConfirmCreateOrganizationModal
      :show="isShowCreateOrganizationModal"
      @close="isShowCreateOrganizationModal = false"
    />
  </portal>
</template>

<script>
import { useElementBounding } from '@vueuse/core'
import { isEmpty, isUndefined } from 'lodash'
import { PortalTarget, Wormhole } from 'portal-vue'
import { computed, defineComponent, ref, useTemplateRef, watch } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import { ROUTE_NAMES } from '@/routes/route-helpers'
import {
  APP_MENU_NAV_LAYOUTS,
  BOTTOM_NAV_NOTIFICATIONS_PORTAL_NAME,
  NAV_ITEM_PLACEMENTS,
  useAppMenu,
  useAppMenuHelpers
} from '@/utils/app-menu'
import { useDisabledModules } from '@/utils/cross-platform/disabled-modules'
import {
  isAzureAppInjectionKey,
  isCrossPlatformAppInjectionKey,
  isJiraAppInjectionKey,
  isWebAppInjectionKey
} from '@/utils/injection-keys'
import { LICENSE_TYPES } from '@/utils/license-types'
import { memoizeGetCssVariableValue } from '@/utils/memoizations'
import { RELEASE_UPDATES_MENU_ITEM_NAME, SUPPORT_MENU_ITEM_NAME } from '@/utils/menu-items'
import { getCurrentPlatformChangelogLinks } from '@/utils/product-update'
import {
  DEFAULT_USERS_SETTINGS,
  RELEASE_UPDATES_MODAL_ID,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'
import ConfirmCreateOrganizationModal from '@shared-modules/components/modals/ConfirmCreateOrganizationModal'
import InviteUsersToWorkspaceModal from '@shared-modules/components/modals/InviteUsersToWorkspaceModal'

import AppHrefLink from '@/components/AppHrefLink'
import AppMenuLogo from '@/components/AppMenu/AppMenuLogo'
import AppMenuNav from '@/components/AppMenu/AppMenuNav'
import AppMenuTrigger from '@/components/AppMenu/AppMenuTrigger'
import AppMenuUserActions from '@/components/AppMenu/AppMenuUserActions'
import AppMenuWorkspaceSelect from '@/components/AppMenu/AppMenuWorkspaceSelect'
import SupportModal from '@/components/support/SupportModal'
import AppBannerWrapper from '@/components/ui/AppBanners/AppBannerWrapper'
import BillingReminder from '@/components/ui/AppBanners/BillingReminder'
import UpgradeTierReminder from '@/components/ui/AppBanners/UpgradeTierReminder'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppSocialMediaLinks from '@/components/ui/AppSocialMediaLinks/AppSocialMediaLinks'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import AppVerticalScrollGradient from '@/components/ui/AppVerticalScrollGradient/AppVerticalScrollGradient'
import Modal from '@/components/ui/Modal/Modal'
import ReleaseUpdatesLoader from '@/components/ui/SkeletonLoaders/ReleaseUpdatesLoader'
import AutomationsPromoBanner from '@/views/workspaces/settings/AutomationsPromoBanner'

export default defineComponent({
  name: 'AppMenu',

  components: {
    AppDivider,
    SupportModal,
    AppButton,
    InviteUsersToWorkspaceModal,
    UpgradeTierReminder,
    BillingReminder,
    ConfirmCreateOrganizationModal,
    AppVerticalScrollGradient,
    AppMenuLogo,
    AppMenuUserActions,
    PortalTarget,
    ReleaseUpdatesLoader,
    AppHrefLink,
    AppSocialMediaLinks,
    AppTitle,
    AppBannerWrapper,
    AppMenuWorkspaceSelect,
    AppMenuTrigger,
    AppMenuNav,
    Modal,
    AutomationsPromoBanner
  },

  inject: {
    isWebApp: {
      from: isWebAppInjectionKey
    },

    isCrossPlatformApp: {
      from: isCrossPlatformAppInjectionKey
    },

    isJiraApp: {
      from: isJiraAppInjectionKey
    },

    isAzureApp: {
      from: isAzureAppInjectionKey
    }
  },

  props: {
    disabled: {
      type: Boolean
    }
  },

  setup() {
    const { menuReference, arrivedState } = useAppMenuHelpers()
    const { isBillingModuleDisabled } = useDisabledModules()
    const menuBodyReference = useTemplateRef('menu-body')
    const { width: menuBodyWidth } = useElementBounding(menuBodyReference)

    const isMenuTruncating = ref(false)

    const appMenuWidth = parseFloat(
      memoizeGetCssVariableValue('--ob-expanded-app-menu-width', '240px')
    )

    const amWrapperRef = useTemplateRef('am-wrapper')
    const { width: amWrapperWidth } = useElementBounding(amWrapperRef)

    watch(
      amWrapperWidth,
      (newValue, oldValue) => {
        if (!newValue || isUndefined(oldValue)) return

        isMenuTruncating.value = newValue < appMenuWidth
      },
      { immediate: true }
    )

    const { isMenuCollapsed, isMenuExpanded, toggleMenu } = useAppMenu()

    return {
      menuReference,
      menuBodyWidth: computed(() => (menuBodyWidth.value ? `${menuBodyWidth.value}px` : 'auto')),
      arrivedState,
      isBillingModuleDisabled,
      isMenuTruncating,
      isMenuCollapsed,
      isMenuExpanded,
      toggleMenu
    }
  },

  data() {
    return {
      someAdditionalMenuOpened: false,
      lastYScrollPosition: 0,
      isShowReleaseUpdatesModal: false,
      isShowSupportModal: false,
      isLoading: true,
      timestamp: Date.now(),
      isShowCreateOrganizationModal: false
    }
  },

  computed: {
    ...mapState('pluginOptions', {
      onboarding: state => state[PLUGIN_OPTIONS_KEYS.ONBOARDING],
      isOwner: state => state[PLUGIN_OPTIONS_KEYS.IS_OWNER],
      licenseStatusId: state => state[PLUGIN_OPTIONS_KEYS.LICENSE_STATUS_ID],
      isPluginAdmin: state => state[PLUGIN_OPTIONS_KEYS.IS_PLUGIN_ADMIN]
    }),

    ...mapGetters('system', {
      releaseUpdatesModalId: 'releaseUpdatesModalId',
      isShowAppMenuPromotionBanner: 'isShowAppMenuPromotionBanner'
    }),

    NAV_ITEM_PLACEMENTS: () => NAV_ITEM_PLACEMENTS,

    APP_MENU_NAV_LAYOUTS: () => APP_MENU_NAV_LAYOUTS,

    BOTTOM_NAV_NOTIFICATIONS_PORTAL_NAME: () => BOTTOM_NAV_NOTIFICATIONS_PORTAL_NAME,

    isBillingPage() {
      return this.$route.name === ROUTE_NAMES.SETTINGS_BILLING_SETTINGS
    },

    isLicenseTrialStatus() {
      return (
        this.licenseStatusId === LICENSE_TYPES.WEB_APP_TRIAL ||
        this.licenseStatusId === LICENSE_TYPES.CROSS_PLATFORM_APP_TRIAL ||
        this.licenseStatusId === LICENSE_TYPES.AZURE_APP_TRIAL
      )
    },

    isOverLimitedLicenseStatus() {
      return (
        this.licenseStatusId === LICENSE_TYPES.WEB_APP_OVERLIMIT ||
        this.licenseStatusId === LICENSE_TYPES.CROSS_PLATFORM_APP_OVERLIMIT ||
        this.licenseStatusId === LICENSE_TYPES.AZURE_APP_OVERLIMIT
      )
    },

    isPortalHasContent() {
      return !isEmpty(Wormhole.getContentForTarget(BOTTOM_NAV_NOTIFICATIONS_PORTAL_NAME))
    },

    currentChangeLogLinks() {
      const { isJiraApp, isWebApp, isCrossPlatformApp, isAzureApp } = this
      return getCurrentPlatformChangelogLinks({
        isJiraApp,
        isWebApp,
        isCrossPlatformApp,
        isAzureApp
      })
    },

    hideWorkspaceSelect() {
      return !!this.$route.meta.hideWorkspaceSelectInAppMenu
    },

    classes() {
      return {
        'am-Content': true,
        'am-Content-additional-menu-opened': this.someAdditionalMenuOpened
      }
    },

    shouldHideBanners() {
      return this.isMenuCollapsed || this.isMenuTruncating
    },

    isShowPromotionBanner() {
      if (this.shouldHideBanners) return false

      return this.isShowAppMenuPromotionBanner && this.isPluginAdmin
    },

    showInternalBillingBanners() {
      if (this.shouldHideBanners) return false

      return this.isWebApp || this.isCrossPlatformApp || this.isAzureApp
    }
  },

  watch: {
    isShowReleaseUpdatesModal: {
      handler(newVal) {
        if (newVal) {
          this.timestamp = Date.now()
        }
      },

      immediate: true
    },

    someAdditionalMenuOpened: {
      handler(newVal) {
        const amContentEl = this.$refs.menuReference
        if (amContentEl) {
          if (newVal) {
            this.lastYScrollPosition = amContentEl.scrollTop
            amContentEl.scrollTo(0, 0)
          } else {
            amContentEl.scrollTo(0, this.lastYScrollPosition)
          }
        }
      },

      immediate: true
    },

    releaseUpdatesModalId: {
      handler(newVal) {
        if (!isUndefined(newVal)) {
          this.resolveReleaseUpdatesModal()
        }
      },

      immediate: true
    }
  },

  methods: {
    ...mapActions('system', {
      updateUserSettings: 'updateUserSettings'
    }),

    onMenuItemClick(item) {
      const { name } = item
      if (name === RELEASE_UPDATES_MENU_ITEM_NAME) {
        this.showReleaseUpdatesModal()
      }

      if (name === SUPPORT_MENU_ITEM_NAME) {
        this.showSupportModal()
      }
    },

    onReleaseUpdatesModalClose() {
      this.isLoading = true
      this.isShowReleaseUpdatesModal = false
    },

    onIframeLoaded() {
      this.isLoading = false
    },

    resolveReleaseUpdatesModal() {
      const currentModalId = DEFAULT_USERS_SETTINGS[RELEASE_UPDATES_MODAL_ID]
      const userSavedModalId = this.releaseUpdatesModalId
      if (userSavedModalId < currentModalId) {
        if (!this.isShowReleaseUpdatesModal && !this.onboarding) {
          this.showReleaseUpdatesModal()
        }
        this.updateUserSettings({
          [USER_SETTINGS_MAPPER[RELEASE_UPDATES_MODAL_ID]]: currentModalId
        })
      }
    },

    showReleaseUpdatesModal() {
      this.isShowReleaseUpdatesModal = true
    },

    showSupportModal() {
      this.isShowSupportModal = true
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/app-menu';

$menu-footer-padding-top: 8px;
$menu-footer-padding-bottom: 16px;
$menu-footer-height: calc(
  #{$menu-footer-padding-top} + #{$menu-footer-padding-bottom} + #{$menu-item-min-height}
);

.am-Wrapper {
  height: 100%;
  max-height: 100vh;
  width: 100%;
  position: sticky;
  top: 0;
  --menu-footer-height: #{$menu-footer-height};
  // box-shadow: inset -2px 0 0 #e6e9ed;
  box-shadow: 0 0 1px 0 rgba(22, 22, 29, 0.31), 0 3px 5px 0 rgba(22, 22, 29, 0.2),
    0 0 60px 0 rgba(0, 0, 0, 0.07);
  transition: box-shadow $menu-transition;

  @media (any-hover: hover) {
    &:hover {
      &:deep(.amt-Trigger) {
        opacity: 1;
      }
    }
  }
  &:deep(.amt-Trigger) {
    opacity: 0;
  }
}

.am-Wrapper_Trigger {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 2;
}

.am-ContentWrapper {
  height: 100%;
  position: relative;
  overflow-x: hidden;
  --ob-am-content-px: 20px;

  &-collapsed {
    --ob-am-content-px: 16px;
  }
}

.am-Content {
  // padding: 0 16px;
  padding-inline: var(--ob-am-content-px);
  transition: padding $menu-transition;
  // height: 100%;
  height: calc(100% - $menu-footer-height);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
  @include styled-native-scrollbar();

  &-additional-menu-opened {
    overflow: hidden;
  }
}

.am-Content_Head {
  @extend %menu-header-styles;
}

.am-Content_Body {
  margin-bottom: auto;
}

/* necessary because sticky + flexbox + overflow-y don't work correct in FF */
// @-moz-document url-prefix() {
//   .am-Content_Body {
//     padding-bottom: var(--footer-height);
//   }
// }

// .am-Footer_Divider {
//   width: 100%;
//   --border-width: 2px;
//   margin: 8px 0;
//   color: #e6e9ed;
//   border-radius: 1px;
// }

.am-Footer_ReleaseUpdatesButton {
  padding: 8px 10px;
  width: 100%;
  font-family: $system-ui;
  font-style: normal;
  font-weight: fw('regular');
  font-size: $fs-14;
  transition: color $menu-transition;
  justify-content: flex-start;
  @extend %nav-item-styles;
}

.am-Content_Footer {
  // display: flex;
  // align-items: center;
  // flex-direction: column;
  // gap: 8px;
  // margin-top: auto;
  padding: $menu-footer-padding-top 0 $menu-footer-padding-bottom;
  overflow-x: hidden;
  position: static;
  // background: linear-gradient(0deg, $white calc(100% - 36px), rgba($white, 0) 100%);
}

.am-LogoLink {
  display: flex;
  padding: 8px;
}

.am-Link {
  font-family: $system-ui;
  font-style: normal;
  font-weight: fw('regular');
  font-size: $fs-12;
  line-height: 16px;
  color: $dark-3;
}

.am-ReleaseUpdatesModal {
  --foter-height: 64px;
  --iframe-content-left-offset: 168.063px;
  &:deep(.uim-ModalHeader) {
    padding: 29px 40px 15px var(--iframe-content-left-offset);
    box-shadow: inset 0 -2px 0 $grey-2-next;
  }
}

.am-ModalLink {
  color: $primary-color-next;
  font-size: $fs-12;
  display: flex;
  line-height: 16px;
  font-weight: fw('regular');
}

.am-ModalHead_Title {
  font-size: $fs-32;
  line-height: 40px;
}

.am-ModalBody_Iframe,
.am-ModalBody_Loader {
  width: 100%;
  display: block;
  height: calc(100% - 84px - var(--foter-height)); // why 84px is modal header height
}

.am-ModalBody_Loader {
  overflow-y: auto;
}

.am-ModalFooter {
  height: var(--foter-height);
  padding: 20px 20px 20px var(--iframe-content-left-offset);
  box-shadow: inset 0 2px 0 $grey-2-next;
  --gap: 20px;
  --main-color: #{$grey-1-next};
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: $system-ui;
  font-size: $fs-12;
  line-height: 16px;
  font-weight: fw('regular');
  color: $dark-3;
}

.am-InviteUsersTrigger {
  flex-shrink: 0;
  color: $primary-color-next;
  border: 2px solid rgba($primary-color-next, 0.2);

  &:hover {
    background-color: rgba($primary-color-next, 0.2);
    border-color: transparent;
  }

  &:active {
    background-color: lighten($primary-color-next, 55%);
  }
}

.am-WebAppMenuContent {
  display: grid;
  gap: 16px;

  &:not(:empty) {
    margin-top: 16px;
  }
}

.am-PromotionBanner {
  position: absolute;
  z-index: 1;
  bottom: calc(var(--menu-footer-height, 64px) + 16px);
  left: var(--ob-am-content-px);
  width: v-bind(menuBodyWidth);
}

.am-Content_Head,
.am-Content_Body,
.am-Gradient {
  @include menu-element-min-width;
}
</style>
